import { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal, Typography } from '@douyinfe/semi-ui';
import { IconCopy, IconKey } from '@douyinfe/semi-icons';
import { api } from './api';
import { pairingError, pairingTicketRequest, type PairingTicket } from './appBossPairing';

export function AppBossPairing({ onClose, onDenied }: { onClose: () => void; onDenied: (error: unknown) => void }) {
  const [name, setName] = useState(''), [origin, setOrigin] = useState('');
  const [ticket, setTicket] = useState<PairingTicket | null>(null), [busy, setBusy] = useState(false);
  const [error, setError] = useState(''), [copied, setCopied] = useState(false), [expired, setExpired] = useState(false);
  const alive = useRef(true), pending = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  useEffect(() => {
    if (!ticket) return;
    const timer = window.setTimeout(() => { setTicket(null); setExpired(true); setCopied(false); }, Math.max(0, Date.parse(ticket.expiresAt) - Date.now()));
    return () => window.clearTimeout(timer);
  }, [ticket]);
  let valid = true;
  try { pairingTicketRequest(name, origin); } catch { valid = false; }
  async function generate() {
    if (pending.current || !valid || ticket) return;
    pending.current = true; setBusy(true); setError('');
    try {
      const result = await api.createAppBossPairingTicket(name, origin);
      if (alive.current) { setTicket(result); setCopied(false); setExpired(false); }
    } catch (value) {
      if (alive.current) {
        setError(pairingError(value));
        if ([401, 403].includes((value as { status?: number })?.status ?? 0)) onDenied(value);
      }
    } finally { pending.current = false; if (alive.current) setBusy(false); }
  }
  async function copy() {
    if (!ticket || Date.parse(ticket.expiresAt) <= Date.now()) return;
    try { await navigator.clipboard.writeText(ticket.code); if (alive.current) setCopied(true); }
    catch { if (alive.current) setError('复制失败，请检查剪贴板权限。'); }
  }
  return <Modal title="连接 AppBoss" visible onCancel={() => { if (!busy) onClose(); }} maskClosable={!busy} closeOnEsc={!busy} width="min(560px, calc(100vw - 24px))" footer={<div className="integration-actions"><Button disabled={busy} onClick={onClose}>关闭</Button>{!ticket && <Button theme="solid" icon={<IconKey />} loading={busy} disabled={!valid || busy} onClick={() => void generate()}>生成配对码</Button>}</div>}>
    <div className="integration-form">
      <label className="integration-field"><span>连接名称</span><Input aria-label="配对连接名称" value={name} maxLength={200} disabled={busy || !!ticket} onChange={setName} /></label>
      <label className="integration-field"><span>AppBoss 服务地址</span><Input aria-label="配对 AppBoss 服务地址" value={origin} placeholder="https://server.appboss.net" disabled={busy || !!ticket} onChange={setOrigin} /></label>
      {ticket && <><label className="integration-field"><span>一次性配对码</span><Input aria-label="一次性配对码" mode="password" readOnly value={ticket.code} autoComplete="off" /></label><Button icon={<IconCopy />} onClick={() => void copy()}>{copied ? '已复制配对码' : '复制配对码'}</Button><Typography.Text type="secondary">有效至 {new Date(ticket.expiresAt).toLocaleTimeString()}。仅可用于上述 AppBoss 地址。</Typography.Text><Typography.Text>在 AppBoss 的 AiGate 设置中填写本服务地址与配对码，点击“连接并启用”。此操作仅连接两台服务，不授权应用、不启用入口，也不修改价格。</Typography.Text></>}
      {expired && <Typography.Text type="warning">配对码已过期。</Typography.Text>}
      {error && <Typography.Text type="danger" role="alert">{error}</Typography.Text>}
    </div>
  </Modal>;
}
